import { useEffect } from 'react';
import { API, setAuthToken } from '../api/api';
import { transformTimelogsToEvents } from '../utils/events';

export function useAuthBootstrap({
  authenticated,
  setAuthenticated,
  setPortalName,
  setEvents,
  showError,
  setError,
  setCurrentView,
  setFeatureFlags,
  setLoginUrl,
}) {
  useEffect(() => {
    const hostSub = window.location.hostname.split('.')[0]?.toUpperCase();
    const pathMatch = window.location.pathname.match(/\/portal\/([A-Z0-9_-]+)/i);
    const defaultPortal = process.env.REACT_APP_DEFAULT_PORTAL?.toUpperCase();
    const portal = pathMatch?.[1]?.toUpperCase() || defaultPortal || hostSub || null;

    if (!portal) {
      setError('No portal specified. Access via /portal/PORTAL_NAME');
      return;
    }

    API.checkAuth()
      .then(async (data) => {
        setPortalName(data.portalName || portal);

        if (setFeatureFlags) {
          setFeatureFlags({
            enableSeries: !!data.enableSeries,
            demoMode: !!data.demoMode,
          });
        }

        if (!data.authenticated) {
          const isMobile = /Mobi|Android/i.test(navigator.userAgent);
          if (isMobile) {
            const { url } = await API.loginPortal(portal);
            window.location.href = url;
            return;
          }
          const { url } = await API.loginPortal(portal);
          if (setLoginUrl) setLoginUrl(url);
          const popup = window.open(url, 'oauthPopup', 'width=600,height=700');
          window.addEventListener('message', function handleOAuthMsg(event) {
            const apiBase = process.env.REACT_APP_API_BASE_URL;
            let backendOrigin = null;
            try { backendOrigin = apiBase ? new URL(apiBase).origin : null; } catch {}
            if (event.origin !== window.location.origin && event.origin !== backendOrigin) return;
            if (event.data && event.data.type === 'oauth-success') {
              const token = event.data.authToken;
              if (token) {
                setAuthToken(token);
                window.location.hash = 'authToken=' + token;
              }
              window.location.reload();
              window.removeEventListener('message', handleOAuthMsg);
            }
          });
          return;
        }

        setAuthenticated(true);

        if (window.location.hash.includes('authToken=')) {
          window.history.replaceState(null, '', window.location.pathname + window.location.search);
        }

        API.getUserViewPreference().then((viewData) => {
          if (viewData.view && setCurrentView) {
            setCurrentView(viewData.view);
          }
        }).catch(() => {});

        API.fetchTimelogs()
          .then((timelogs) => {
            setEvents(transformTimelogsToEvents(timelogs.data || []));
          })
          .catch(() => {
            showError('Data Error', 'Failed to fetch timelogs. Please try refreshing the page.');
          });
      })
      .catch(() => {
        setError('Connection error - please try again');
      });
  }, [showError]);

  useEffect(() => {
    if (!authenticated) return;
    const interval = setInterval(() => {
      API.ping().catch(() => {
        window.location.reload();
      });
    }, 30000);
    return () => clearInterval(interval);
  }, [authenticated]);
}
